import { createApi } from '@reduxjs/toolkit/query/react';
import { debounce, findIndex } from 'lodash';
import { AppDispatch } from 'store';
import {
  AddAndRemoveQueueMemberDto,
  AgentDto,
  AgentStatusDto,
  AssignChatSessionDto,
  CallSessionDto,
  ChatAgentStatusDto,
  ChatMonitoringUserDto,
  ChatSessionDto,
  CompleteOmniChannelSessionDto,
  CountCallSessionDto,
  CountCallSessionResultDto,
  CountChatSessionDto,
  CountChatSessionResultDto,
  CreateOmniChannelMessageDto,
  FindAllCallSessionDto,
  FindAllChatSessionsHistoryDto,
  FindAllDto,
  FindAllOmniCustomersDto,
  FindAllOmniMessagesDto,
  FindAllSpeakToTextDto,
  FindWithKeywordDto,
  GetAgentStatusDto,
  GetCallDetailsDto,
  GetOmniMessageResponseDto,
  GetOmniSpeakToTextResponseDto,
  GetSoftPhoneTokenResponseDto,
  GetWaitingCallsDto,
  HideCommentDto,
  ListQueueDto,
  OmniChannelMessageDto,
  OmniCustomerDto,
  QueueMemberDto,
  QueueMemberResponseDto,
  QueueNameDto,
  ReadOmniChannelMessageDto,
  ResponseDto,
  ResponsePagingDto,
  SourceOptionDto,
  SpeakToTextDto,
  TransferChatSessionDto,
  UpdateCallSessionDto
} from 'types';
import { FindAllChatSessions, OmniChannelCallGroup, OmniChannelFanpageDto, OmniChannelGatewayConfigDto } from 'types';
import {
  CallSessionStatus,
  CHAT_STATUS,
  convertMetadata,
  extractUrls,
  fetchMetadata,
  includesRemoveSpecial,
  mergeArrays,
  mergeArrays2,
  MESSAGE_LIMIT_CURRENT,
  MESSAGE_LIMIT_LOAD,
  OMNI_MESSAGE_QUERY_TYPE
} from 'utils';
import axiosBaseQuery from 'utils/base-api';
import {
  offReceiveCallSessionAiProgressing,
  offReceiveChatMonitoringUser,
  offReceiveChatSessionAIInProgress,
  offReceiveChatSessionInProgress,
  offReceiveChatSessionWaiting,
  offRemoveChatMonitoringUser,
  offRemoveChatSessionAIInProgress,
  offRemoveChatSessionInProgress,
  offRemoveChatSessionWaiting,
  onReceiveCallSessionAiProgressing,
  onReceiveChatMonitoringUser,
  onReceiveChatSessionAIInProgress,
  onReceiveChatSessionInProgress,
  onReceiveChatSessionProcessing,
  onReceiveChatSessionWaiting,
  onReceiveTicketInfo,
  onRemoveChatMonitoringUser,
  onRemoveChatSessionAIInProgress,
  onRemoveChatSessionInProgress,
  onRemoveChatSessionWaiting
} from './omni-channel.socket-client';
import { GetWorldPhoneTokenDto } from 'types';
import { AddAndRemoveQueueMemberResponseDto } from 'types/dto/omni-channel/add-and-remove-queue-member-response.dto';
import { CallHistoryDto } from 'types/dto/omni-channel/call-history.dto';
import { GetCallHistoryDto } from 'types/dto/omni-channel/get-call-history.dto';
import { PlayRecordResponseDto } from 'types/dto/omni-channel/play-record.dto';
import { GetPlayRecordDto } from 'types/dto/omni-channel/get-play-record.dto';
import { FindAllTicketAIAgentDto, TicketDto } from 'types/dto/ticket';

export const omniChannelApi = createApi({
  reducerPath: 'omniChannelApi',
  tagTypes: [
    'omni_channel_gateway_config',
    'omni_channel_group_calls',
    'omni_channel_fanpages',
    'omni_channel_chat_session_processing',
    'omni_channel_chat_session_waiting',
    'omni_channel_message',
    'omni_channel_soft_phone_token',
    'omni_channel_agents',
    'omni_channel_chat_agent_status',
    'omni_customers',
    'omni_channel_chat_session_history',
    'omni_source_options',
    'omni_channel_chat_session_supervisor_inprogress',
    'omni_channel_chat_session_supervisor_histories',
    'chat_monitoring_user',
    'tickets-ai-agent',
    'omni_channel_call_session_inprogress',
    'omni_channel_call_session_histories',
    'omni_channel_message_speak_to_texts',
    'chat_session_detail',
    'call_session_detail'
  ],
  baseQuery: axiosBaseQuery,
  endpoints: (builder) => ({
    getGroupCalls: builder.query<ResponseDto<OmniChannelCallGroup[]>, void>({
      query: () => ({
        url: '/omni_channel/get_group_call',
        method: 'get'
      }),
      providesTags: ['omni_channel_group_calls']
    }),
    getGatewayConfig: builder.query<ResponseDto<OmniChannelGatewayConfigDto>, void>({
      query: () => ({
        url: '/omni_channel/omni_gateway_config',
        method: 'get'
      }),
      providesTags: ['omni_channel_gateway_config']
    }),
    getFanpages: builder.query<ResponsePagingDto<OmniChannelFanpageDto>, void>({
      query: () => ({
        url: '/omni_channel/fanpage',
        method: 'get'
      }),
      providesTags: ['omni_channel_fanpages']
    }),

    getChatSessionsWaiting: builder.query<ResponsePagingDto<ChatSessionDto>, FindAllChatSessions>({
      query: ({ endpoint, ...params }) => ({
        url: '/omni_channel/chat_sessions/waiting',
        method: 'get',
        params
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        if (queryArgs.endpoint) {
          return endpointName + queryArgs.endpoint;
        }
        return endpointName;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.skip !== 0) {
          currentCache.data.rows = mergeArrays(currentCache.data.rows, newItems.data.rows, 'chatSessionId');
        } else currentCache.data.rows = newItems.data.rows;
        currentCache.data.count = newItems.data.count;
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.keyword !== previousArg?.keyword || currentArg?.skip !== previousArg?.skip;
      },
      providesTags: (result) =>
        result && result.data.rows.length > 0
          ? result.data.rows.map(({ chatSessionId }) => ({
              type: 'omni_channel_chat_session_waiting',
              id: chatSessionId
            }))
          : ['omni_channel_chat_session_waiting'],

      async onCacheEntryAdded(arg, { cacheDataLoaded, updateCachedData, cacheEntryRemoved, getState }) {
        try {
          await cacheDataLoaded;

          onReceiveChatSessionWaiting((data) => {
            if (data && data.chatSessionId) {
              debounce(() => {
                updateCachedData((draft) => {
                  const stateQueries = getState().omniChannelApi.queries;

                  const queryKey = 'getChatSessionsWaiting' + (arg.endpoint || '');
                  const dataQueries = stateQueries[queryKey];
                  const originalArgs = dataQueries?.originalArgs as FindAllChatSessions;

                  const chatId = data.chatId;
                  const existingIndex = draft.data.rows.findIndex((chatSession) => chatSession.chatId === chatId);

                  if (existingIndex !== -1) {
                    if (data.status === CHAT_STATUS.WAITING) {
                      // Thay thế nếu data có trong danh sách và đang ở trạng thái WAITING
                      draft.data.rows[existingIndex] = data;
                    } else {
                      // Xóa nếu không ở trạng thái WAITING
                      draft.data.rows.splice(existingIndex, 1);
                      draft.data.count--;
                    }
                  } else if (data.status === CHAT_STATUS.WAITING) {
                    if (!originalArgs?.keyword || includesRemoveSpecial(data.chat.customerName, originalArgs.keyword)) {
                      draft.data.rows.push(data);
                      draft.data.count++;
                    }
                  }
                });
              }, 100)();
            }
          });

          onRemoveChatSessionWaiting((data) => {
            if (data && data.chatSessionId) {
              debounce(() => {
                updateCachedData((draft) => {
                  const chatId = data.chatId;
                  const existingIndex = draft.data.rows.findIndex((chatSession) => chatSession.chatId === chatId);

                  if (existingIndex !== -1) {
                    // Xóa nếu data có trong danh sách
                    draft.data.rows.splice(existingIndex, 1);
                    draft.data.count--;
                  }
                });
              }, 100)();
            }
          });
        } catch (err) {
          console.error({ err });
        }

        await cacheEntryRemoved;
        offReceiveChatSessionWaiting();
        offRemoveChatSessionWaiting();
      }
    }),

    getChatSessionsProcessing: builder.query<ResponsePagingDto<ChatSessionDto>, FindAllChatSessions>({
      query: (params) => ({
        url: '/omni_channel/chat_sessions/processing',
        method: 'get',
        params
      }),
      serializeQueryArgs: ({ endpointName }) => endpointName,
      merge: (currentCache, newItems, { arg }) => {
        if (arg.skip !== 0) {
          currentCache.data.rows = mergeArrays(currentCache.data.rows, newItems.data.rows, 'chatSessionId');
        } else currentCache.data.rows = newItems.data.rows;
        currentCache.data.count = newItems.data.count;
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.keyword !== previousArg?.keyword || currentArg?.skip !== previousArg?.skip;
      },
      providesTags: (result) =>
        result && result.data.rows.length > 0
          ? result.data.rows.map(({ chatSessionId }) => ({
              type: 'omni_channel_chat_session_processing',
              id: chatSessionId
            }))
          : ['omni_channel_chat_session_processing'],
      async onCacheEntryAdded(arg, { cacheDataLoaded, updateCachedData, cacheEntryRemoved, getState }) {
        try {
          await cacheDataLoaded;

          onReceiveChatSessionProcessing((data) => {
            if (data && data.chatSessionId) {
              debounce(() => {
                updateCachedData((draft) => {
                  const chatId = data.chatId;
                  const stateQueries = getState().omniChannelApi.queries;

                  const queryKey = 'getChatSessionsWaiting' + (arg.endpoint ?? '');
                  const dataQueries = stateQueries[queryKey];
                  const originalArgs = dataQueries?.originalArgs as FindAllChatSessions | undefined;

                  const existingIndex = draft.data.rows.findIndex((chatSession) => chatSession.chatId === chatId);

                  if (existingIndex !== -1) {
                    if (data.status === CHAT_STATUS.COMPLETED) {
                      // Xóa nếu trạng thái là COMPLETED
                      draft.data.rows.splice(existingIndex, 1);
                      draft.data.count--;
                    } else {
                      // Cập nhật nếu đã tồn tại
                      draft.data.rows[existingIndex] = { ...data };
                    }
                  } else if (data.status !== CHAT_STATUS.COMPLETED) {
                    if (!originalArgs?.keyword || includesRemoveSpecial(data.chat.customerName, originalArgs.keyword)) {
                      draft.data.rows.push(data);
                      draft.data.count++;
                    }
                  }
                });
              }, 100)();
            }
          });
        } catch (err) {
          console.error({ err });
        }

        await cacheEntryRemoved;
        // offReceiveChatSessionProcessing();
      }
    }),

    getOmniChatMessages: builder.query<GetOmniMessageResponseDto, FindAllOmniMessagesDto>({
      query: ({ ...params }) => ({
        url: '/omni_channel/messages',
        method: 'get',
        params
      }),

      async transformResponse(response: GetOmniMessageResponseDto) {
        const updatedRows = await addMetadataToOmniMessages(response.data.rows);
        return {
          ...response,
          data: {
            ...response.data,
            rows: updatedRows.map((o) => ({
              ...o,
              senderId: isNaN(Number(o.senderId)) ? o.senderId : Number(o.senderId)
            }))
          }
        };
      },

      serializeQueryArgs: ({ endpointName, queryArgs }) => endpointName + queryArgs.chatId,
      merge: (currentCache, newItems, { arg }) => {
        if (arg?.startMessageId) {
          let currentRows = [...currentCache.data.rows];
          if (arg.sortOrder === OMNI_MESSAGE_QUERY_TYPE.NEWER) {
            currentRows =
              currentRows.length >= MESSAGE_LIMIT_CURRENT ? currentRows.slice(MESSAGE_LIMIT_LOAD) : currentRows;
            currentCache.data.rows = [...currentRows, ...newItems.data.rows];
          } else {
            currentRows =
              currentRows.length >= MESSAGE_LIMIT_CURRENT ? currentRows.slice(0, -MESSAGE_LIMIT_LOAD) : currentRows;
            currentCache.data.rows = [...newItems.data.rows, ...currentRows];
          }
          currentCache.data.count = newItems.data.count;
          currentCache.newMess = newItems.data.rows;
          return;
        }

        currentCache.data.count = newItems.data.count;
        currentCache.newMess = newItems.data.rows;
        currentCache.data.rows = newItems.data.rows;
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.chatId !== previousArg?.chatId;
      },
      providesTags: (result) =>
        result && result.data.rows.length > 0
          ? result.data.rows.map(({ messageId, chatId }) => ({
              type: 'omni_channel_message',
              id: chatId
            }))
          : ['omni_channel_message']
    }),

    receiveChatSession: builder.mutation<ResponseDto<ChatSessionDto>, void>({
      query: () => ({
        url: '/omni_channel/receive_chat_session',
        method: 'post'
      }),
      invalidatesTags: ['omni_channel_chat_session_waiting', 'omni_channel_chat_session_processing']
    }),

    sendOmniChatMessage: builder.mutation<ResponseDto<OmniChannelMessageDto>, CreateOmniChannelMessageDto>({
      query: (newChat) => ({
        url: '/omni_channel/create_message',
        method: 'post',
        data: newChat
      }),
      transformResponse: (response: ResponseDto<OmniChannelMessageDto>) => {
        return {
          ...response,
          data: {
            ...response.data,
            senderId: isNaN(Number(response.data.senderId)) ? response.data.senderId : Number(response.data.senderId)
          }
        };
      }
    }),

    readOmniChatMessages: builder.mutation<ResponseDto<ChatSessionDto>, ReadOmniChannelMessageDto>({
      query: ({ chatSessionId, ...data }) => ({
        url: `/omni_channel/read_messages/${chatSessionId}`,
        method: 'post',
        data: data
      })
    }),

    completeChatSession: builder.mutation<ResponseDto<ChatSessionDto>, CompleteOmniChannelSessionDto>({
      query: ({ chatSessionId, ...data }) => ({
        url: `/omni_channel/complete_chat_session/${chatSessionId}`,
        method: 'post',
        data: data
      }),
      invalidatesTags: ['omni_channel_chat_session_waiting', 'omni_channel_chat_session_processing']
    }),

    getChatSessionsHistories: builder.query<ResponsePagingDto<ChatSessionDto>, FindAllChatSessionsHistoryDto>({
      query: (params) => ({
        url: '/omni_channel/chat_sessions/history',
        method: 'get',
        params
      }),
      serializeQueryArgs: ({ endpointName }) => endpointName,
      merge: (currentCache, newItems, { arg }) => {
        if (!!arg.skip && arg.skip !== 0) {
          currentCache.data.rows = mergeArrays(currentCache.data.rows, newItems.data.rows, 'chatSessionId');
        } else currentCache.data.rows = newItems.data?.rows;
        currentCache.data.count = newItems.data.count;
      },
      forceRefetch({ currentArg, previousArg }) {
        return (
          currentArg?.startChatSessionId !== previousArg?.startChatSessionId ||
          currentArg?.fromStartTime !== previousArg?.fromStartTime ||
          currentArg?.toStartTime !== previousArg?.toStartTime ||
          currentArg?.fromEndTime !== previousArg?.fromEndTime ||
          currentArg?.toEndTime !== previousArg?.toEndTime ||
          currentArg?.extensionId !== previousArg?.extensionId ||
          currentArg?.customerId !== previousArg?.customerId ||
          currentArg?.skip !== previousArg?.skip ||
          currentArg?.limit !== previousArg?.limit ||
          currentArg?.source !== previousArg?.source
        );
      },
      providesTags: ['omni_channel_chat_session_history']
    }),

    getChatSessionsSupervisorInprogress: builder.query<
      ResponsePagingDto<ChatSessionDto>,
      FindAllChatSessionsHistoryDto
    >({
      query: (params) => ({
        url: '/omni_channel/chat_sessions/supervisor/in_progress',
        method: 'get',
        params
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        // Thêm endpoint vào serializeQueryArgs giống như getChatSessionsWaiting
        if (queryArgs.endpoint) {
          return endpointName + queryArgs.endpoint;
        }
        return endpointName;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (!!arg.skip && arg.skip !== 0) {
          currentCache.data.rows = mergeArrays(currentCache.data.rows, newItems.data.rows, 'chatSessionId');
        } else currentCache.data.rows = newItems.data?.rows;
        currentCache.data.count = newItems.data.count;
      },
      forceRefetch({ currentArg, previousArg }) {
        return (
          currentArg?.startChatSessionId !== previousArg?.startChatSessionId ||
          currentArg?.fromStartTime !== previousArg?.fromStartTime ||
          currentArg?.toStartTime !== previousArg?.toStartTime ||
          currentArg?.fromEndTime !== previousArg?.fromEndTime ||
          currentArg?.toEndTime !== previousArg?.toEndTime ||
          currentArg?.extensionId !== previousArg?.extensionId ||
          currentArg?.customerId !== previousArg?.customerId ||
          currentArg?.skip !== previousArg?.skip ||
          currentArg?.limit !== previousArg?.limit ||
          currentArg?.source !== previousArg?.source ||
          currentArg?.isHandleByAi !== previousArg?.isHandleByAi ||
          currentArg?.keyword !== previousArg?.keyword ||
          currentArg?.endpoint !== previousArg?.endpoint
        );
      },
      providesTags: ['omni_channel_chat_session_supervisor_inprogress'],
      async onCacheEntryAdded(arg, { cacheDataLoaded, updateCachedData, cacheEntryRemoved, getState }) {
        try {
          await cacheDataLoaded;

          onReceiveChatSessionInProgress((data) => {
            console.log('Received chat session in progress via socket:', data);
            if (data && data.chatSessionId) {
              debounce(() => {
                updateCachedData((draft) => {
                  const stateQueries = getState().omniChannelApi.queries;

                  const queryKey = 'getChatSessionsSupervisorInprogress' + (arg.endpoint || '');
                  const dataQueries = stateQueries[queryKey];
                  const originalArgs = dataQueries?.originalArgs as FindAllChatSessionsHistoryDto;

                  const filterAi = originalArgs?.isHandleByAi || false;
                  const dataByAi = data.isHandleByAi || false;

                  if (filterAi !== dataByAi) return;

                  const chatId = data.chatId;
                  const existingIndex = draft.data.rows.findIndex((chatSession) => chatSession.chatId === chatId);

                  if (existingIndex !== -1) {
                    if (data.status === CHAT_STATUS.PROCESSING) {
                      // Thay thế nếu data có trong danh sách và đang ở trạng thái PROCESSING
                      draft.data.rows[existingIndex] = data;
                    } else {
                      // Xóa nếu không ở trạng thái PROCESSING
                      draft.data.rows.splice(existingIndex, 1);
                      draft.data.count--;
                    }
                  } else if (data.status === CHAT_STATUS.PROCESSING) {
                    if (
                      (!originalArgs?.extensionId || data.extensionId === originalArgs?.extensionId) &&
                      (!originalArgs?.source || data.chat.source === originalArgs?.source)
                    ) {
                      draft.data.rows.push(data);
                      draft.data.count++;
                    }
                  }
                });
              }, 100)();
            }
          });

          onReceiveChatSessionAIInProgress((data) => {
            if (data && data.chatSessionId) {
              debounce(() => {
                updateCachedData((draft) => {
                  const stateQueries = getState().omniChannelApi.queries;

                  const queryKey = 'getChatSessionsSupervisorInprogress' + (arg.endpoint || '');
                  const dataQueries = stateQueries[queryKey];
                  const originalArgs = dataQueries?.originalArgs as FindAllChatSessionsHistoryDto;
                  const filterAi = originalArgs?.isHandleByAi || false;
                  const dataByAi = data.isHandleByAi || false;
                  if (filterAi !== dataByAi) return;

                  const chatId = data.chatId;
                  const existingIndex = draft.data.rows.findIndex((chatSession) => chatSession.chatId === chatId);

                  if (existingIndex !== -1) {
                    if (data.status === CHAT_STATUS.PROCESSING) {
                      // Thay thế nếu data có trong danh sách và đang ở trạng thái PROCESSING
                      draft.data.rows[existingIndex] = data;
                    } else {
                      // Xóa nếu không ở trạng thái PROCESSING
                      draft.data.rows.splice(existingIndex, 1);
                      draft.data.count--;
                    }
                  } else if (data.status === CHAT_STATUS.PROCESSING) {
                    const isValid =
                      (!originalArgs?.extensionId || data.extensionId === originalArgs?.extensionId) &&
                      (!originalArgs?.fromStartTime ||
                        (data.receivedAt && new Date(data.receivedAt) >= new Date(originalArgs.fromStartTime))) &&
                      (!originalArgs?.toEndTime ||
                        (data.receivedAt && new Date(data.receivedAt) <= new Date(originalArgs.toEndTime)));

                    if (isValid) {
                      draft.data.rows.push(data);
                      draft.data.count++;
                    }
                  }
                });
              }, 100)();
            }
          });

          onRemoveChatSessionInProgress((data) => {
            if (data && data.chatSessionId) {
              debounce(() => {
                updateCachedData((draft) => {
                  const chatId = data.chatId;
                  const existingIndex = draft.data.rows.findIndex((chatSession) => chatSession.chatId === chatId);

                  if (existingIndex !== -1) {
                    // Xóa nếu data có trong danh sách
                    draft.data.rows.splice(existingIndex, 1);
                    draft.data.count--;
                  }
                });
              }, 100)();
            }
          });

          onRemoveChatSessionAIInProgress((data) => {
            if (data && data.chatSessionId) {
              debounce(() => {
                updateCachedData((draft) => {
                  const chatId = data.chatId;
                  const existingIndex = draft.data.rows.findIndex((chatSession) => chatSession.chatId === chatId);

                  if (existingIndex !== -1) {
                    // Xóa nếu data có trong danh sách
                    draft.data.rows.splice(existingIndex, 1);
                    draft.data.count--;
                  }
                });
              }, 100)();
            }
          });
        } catch (err) {
          console.error({ err });
        }

        await cacheEntryRemoved;
        offReceiveChatSessionInProgress();
        offReceiveChatSessionAIInProgress();
        offRemoveChatSessionInProgress();
        offRemoveChatSessionAIInProgress();
      }
    }),

    getChatSessionsSupervisorHistories: builder.query<ResponsePagingDto<ChatSessionDto>, FindAllChatSessionsHistoryDto>(
      {
        query: (params) => ({
          url: '/omni_channel/chat_sessions/supervisor/history',
          method: 'get',
          params
        }),
        serializeQueryArgs: ({ endpointName }) => endpointName,
        merge: (currentCache, newItems, { arg }) => {
          if (!!arg.skip && arg.skip !== 0) {
            currentCache.data.rows = mergeArrays(currentCache.data.rows, newItems.data.rows, 'chatSessionId');
          } else currentCache.data.rows = newItems.data?.rows;
          currentCache.data.count = newItems.data.count;
        },
        forceRefetch({ currentArg, previousArg }) {
          return (
            currentArg?.startChatSessionId !== previousArg?.startChatSessionId ||
            currentArg?.fromStartTime !== previousArg?.fromStartTime ||
            currentArg?.toStartTime !== previousArg?.toStartTime ||
            currentArg?.fromEndTime !== previousArg?.fromEndTime ||
            currentArg?.toEndTime !== previousArg?.toEndTime ||
            currentArg?.extensionId !== previousArg?.extensionId ||
            currentArg?.customerId !== previousArg?.customerId ||
            currentArg?.skip !== previousArg?.skip ||
            currentArg?.limit !== previousArg?.limit ||
            currentArg?.source !== previousArg?.source ||
            currentArg?.isHandleByAi !== previousArg?.isHandleByAi
          );
        },
        providesTags: ['omni_channel_chat_session_supervisor_histories']
      }
    ),

    getSoftPhoneToken: builder.mutation<ResponseDto<GetSoftPhoneTokenResponseDto>, GetWorldPhoneTokenDto>({
      query: (getSoftPhoneTokenDto) => ({
        url: '/omni_channel/get_soft_phone_token',
        method: 'post',
        data: getSoftPhoneTokenDto
      })
    }),
    getAgentStatus: builder.query<ResponseDto<AgentStatusDto[]>, GetAgentStatusDto>({
      query: ({ ...params }) => ({
        url: '/omni_channel/agent_status',
        method: 'GET',
        params
      })
    }),
    unpauseQueueAgent: builder.mutation<ResponseDto<QueueMemberResponseDto>, QueueMemberDto>({
      query: (getSoftPhoneTokenDto) => ({
        url: '/omni_channel/unpause_queue',
        method: 'post',
        data: getSoftPhoneTokenDto
      })
    }),

    pauseQueueAgent: builder.mutation<ResponseDto<QueueMemberResponseDto>, QueueMemberDto>({
      query: (getSoftPhoneTokenDto) => ({
        url: '/omni_channel/pause_queue',
        method: 'post',
        data: getSoftPhoneTokenDto
      })
    }),
    listQueueName: builder.query<ResponseDto<QueueNameDto[]>, void>({
      query: (listQueueName) => ({
        url: '/omni_channel/list_queue_name',
        method: 'get',
        data: listQueueName
      })
    }),
    listQueue: builder.query<ResponseDto<ListQueueDto[]>, void>({
      query: (listQueueName) => ({
        url: '/omni_channel/list_queue',
        method: 'get',
        data: listQueueName
      })
    }),
    addQueueMember: builder.mutation<ResponseDto<AddAndRemoveQueueMemberResponseDto[]>, AddAndRemoveQueueMemberDto>({
      query: (listQueueName) => ({
        url: '/omni_channel/add_queue_member',
        method: 'post',
        data: listQueueName
      })
    }),
    removeQueueMember: builder.mutation<ResponseDto<AddAndRemoveQueueMemberResponseDto[]>, AddAndRemoveQueueMemberDto>({
      query: (listQueueName) => ({
        url: '/omni_channel/remove_queue_member',
        method: 'post',
        data: listQueueName
      })
    }),
    getCallDetails: builder.query<ResponseDto<GetCallDetailsDto[]>, void>({
      query: (details) => ({
        url: '/omni_channel/get_call_details',
        method: 'get'
      })
    }),
    getWaitingCalls: builder.query<ResponseDto<GetWaitingCallsDto>, void>({
      query: (details) => ({
        url: '/omni_channel/get_waiting_calls',
        method: 'get'
      })
    }),
    countChatSession: builder.query<ResponseDto<CountChatSessionResultDto>, CountChatSessionDto>({
      query: ({ ...params }) => ({
        url: '/omni_channel/count_chat_session',
        method: 'get',
        params
      })
    }),
    assignChatSession: builder.mutation<ResponseDto<ChatSessionDto>, AssignChatSessionDto>({
      query: (data) => ({
        url: '/omni_channel/assign_chat_session',
        method: 'post',
        data: data
      }),
      invalidatesTags: ['omni_channel_chat_session_waiting', 'omni_channel_chat_session_processing']
    }),
    transferChatSession: builder.mutation<ResponseDto<ChatSessionDto>, TransferChatSessionDto>({
      query: (data) => ({
        url: '/omni_channel/transfer_chat_session',
        method: 'post',
        data: data
      }),
      invalidatesTags: ['omni_channel_chat_session_waiting', 'omni_channel_chat_session_processing']
    }),
    getAgents: builder.query<ResponsePagingDto<AgentDto>, FindWithKeywordDto>({
      query: ({ endpoint, ...params }) => ({
        url: '/omni_channel/agents',
        method: 'get',
        params
      }),
      providesTags: ['omni_channel_agents']
    }),
    likeComment: builder.mutation<ResponseDto<OmniChannelMessageDto>, number>({
      query: (messageId) => ({
        url: `/omni_channel/like_comment/${messageId}`,
        method: 'post'
      })
    }),
    removeComment: builder.mutation<ResponseDto<OmniChannelMessageDto>, number>({
      query: (messageId) => ({
        url: `/omni_channel/remove_comment/${messageId}`,
        method: 'post'
      })
    }),
    hideComment: builder.mutation<ResponseDto<OmniChannelMessageDto>, HideCommentDto>({
      query: ({ messageId, ...data }) => ({
        url: `/omni_channel/hide_comment/${messageId}`,
        method: 'post',
        data
      })
    }),
    createChatMessenger: builder.mutation<ResponseDto<ChatSessionDto>, number>({
      query: (chatSessionId) => ({
        url: `/omni_channel/create_chat_session_messenger/${chatSessionId}`,
        method: 'post'
      })
    }),

    getChatAgentStatus: builder.query<ResponseDto<ChatAgentStatusDto>, void>({
      query: () => ({
        url: '/omni_channel/chat_agent_status',
        method: 'get'
      }),
      providesTags: ['omni_channel_chat_agent_status']
    }),

    updateChatAgentStatus: builder.mutation<ResponseDto<ChatAgentStatusDto>, Pick<ChatAgentStatusDto, 'status'>>({
      query: (data) => ({
        url: '/omni_channel/chat_agent_status',
        method: 'put',
        data
      }),
      invalidatesTags: ['omni_channel_chat_agent_status']
    }),
    getCallHistory: builder.query<ResponsePagingDto<CallHistoryDto>, GetCallHistoryDto>({
      query: (params) => ({
        url: '/omni_channel/call_history',
        method: 'get',
        params
      })
    }),
    getVoiceRecord: builder.query<ResponseDto<PlayRecordResponseDto>, GetPlayRecordDto>({
      query: (params) => ({
        url: '/omni_channel/play_record',
        method: 'get',
        params
      })
    }),
    getCustomers: builder.query<ResponsePagingDto<OmniCustomerDto>, FindAllOmniCustomersDto>({
      query: (params) => ({
        url: '/omni_channel/customers',
        method: 'get',
        params
      }),
      serializeQueryArgs: ({ endpointName }) => endpointName,
      merge: (currentCache, newItems, { arg }) => {
        if (!!arg.skip && arg.skip !== 0) {
          currentCache.data.rows = mergeArrays2(currentCache.data.rows, newItems.data.rows, 'customerId', 'pageId');
        } else currentCache.data.rows = newItems.data?.rows;
        currentCache.data.count = newItems.data.count;
      },
      forceRefetch({ currentArg, previousArg }) {
        return (
          currentArg?.keyword !== previousArg?.keyword ||
          currentArg?.skip !== previousArg?.skip ||
          currentArg?.limit !== previousArg?.limit ||
          currentArg?.endpoint !== previousArg?.endpoint
        );
      },
      providesTags: ['omni_customers']
    }),
    getSourceOptions: builder.query<ResponsePagingDto<SourceOptionDto>, FindAllDto>({
      query: (params) => ({
        url: '/omni_channel/source/options',
        method: 'get',
        params
      }),
      serializeQueryArgs: ({ endpointName }) => endpointName,
      merge: (currentCache, newItems, { arg }) => {
        if (arg.pageIndex !== 1) {
          currentCache.data.rows.push(...newItems.data.rows);
        } else currentCache.data.rows = newItems.data.rows;
        currentCache.data.count = newItems.data.count;
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.keyword !== previousArg?.keyword || currentArg?.pageIndex !== previousArg?.pageIndex;
      },
      providesTags: ['omni_source_options']
    }),
    countAssignerProcessingChatSessions: builder.query<ResponseDto<number>, number>({
      query: (assignerId) => ({
        url: `/omni_channel/count_assigner_processing_chat_sessions/${assignerId}`,
        method: 'get',
        params: { assignerId }
      })
    }),
    getChatMonitoringUser: builder.query<ResponsePagingDto<ChatMonitoringUserDto>, void>({
      query: () => ({
        url: `/omni_channel/monitoring/users`,
        method: 'get'
      }),
      serializeQueryArgs: ({ endpointName }) => endpointName,
      providesTags: () => [{ type: 'chat_monitoring_user' }],
      async onCacheEntryAdded(arg, { cacheDataLoaded, updateCachedData, cacheEntryRemoved, getState }) {
        try {
          await cacheDataLoaded;
          onReceiveChatMonitoringUser((data) => {
            if (data && data.chatSessionId) {
              debounce(() => {
                updateCachedData((draft) => {
                  const exitsChatMonitoringUserIndex = draft.data.rows.findIndex(
                    (chatMonitoringUser) => chatMonitoringUser.chatSessionId === data.chatSessionId
                  );
                  if (exitsChatMonitoringUserIndex !== -1) {
                    const users = draft.data.rows[exitsChatMonitoringUserIndex].users;
                    const i = users.findIndex((u) => u.userId === data.user.userId);
                    i >= 0 ? (users[i] = data.user) : users.push(data.user);
                    draft.data.rows[exitsChatMonitoringUserIndex].users = users;
                  } else {
                    draft.data.rows.push({
                      chatSessionId: data.chatSessionId,
                      users: [data.user]
                    });
                  }
                });
              }, 100)();
            }
          });

          onRemoveChatMonitoringUser((data) => {
            if (data && data.chatSessionId) {
              debounce(() => {
                updateCachedData((draft) => {
                  const exitsChatMonitoringUserIndex = draft.data.rows.findIndex(
                    (chatMonitoringUser) => chatMonitoringUser.chatSessionId === data.chatSessionId
                  );
                  if (exitsChatMonitoringUserIndex !== -1) {
                    const users = draft.data.rows[exitsChatMonitoringUserIndex].users;
                    const i = users.findIndex((u) => u.userId === data.userId);
                    if (i >= 0) {
                      users.splice(i, 1);
                      draft.data.rows[exitsChatMonitoringUserIndex].users = users;
                    }
                    if (users.length === 0) {
                      draft.data.rows.splice(exitsChatMonitoringUserIndex, 1);
                    }
                  }
                });
              }, 100)();
            }
          });
        } catch (err) {
          console.error('monitoring chat user error', err);
        }

        await cacheEntryRemoved;
        offReceiveChatMonitoringUser();
        offRemoveChatMonitoringUser();
      }
    }),
    getTicketsAIAgent: builder.query<ResponsePagingDto<TicketDto>, FindAllTicketAIAgentDto>({
      query: (params) => ({
        url: '/tickets/ai_agent',
        method: 'get',
        params
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return queryArgs.isMore ? endpointName : endpointName + JSON.stringify(queryArgs);
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.pageIndex !== 1 && arg.isMore) {
          currentCache.data.rows = mergeArrays(currentCache.data.rows, newItems.data.rows, 'ticketId');
        } else currentCache.data.rows = newItems.data.rows;
        currentCache.data.count = newItems.data.count;
      },
      providesTags: (result) =>
        result && result.data?.rows.length > 0
          ? result.data?.rows?.map(({ ticketId }) => ({
              type: 'tickets-ai-agent',
              id: ticketId
            }))
          : ['tickets-ai-agent'],
      async onCacheEntryAdded(arg, { cacheDataLoaded, updateCachedData, cacheEntryRemoved, getState }) {
        try {
          await cacheDataLoaded;
          onReceiveTicketInfo((data) => {
            debounce(() => {
              updateCachedData((draft) => {
                const ticketIndex = draft.data.rows.findIndex((t) => t.ticketId === data.ticket.ticketId);
                if (ticketIndex !== -1) {
                  draft.data.rows[ticketIndex] = {
                    ...draft.data.rows[ticketIndex],
                    ...data.ticket
                  };
                } else {
                  draft.data.rows.unshift(data.ticket);
                  draft.data.count += 1;
                }
              });
            });
          });
        } catch (error) {
          // Handle error if needed
        }
        await cacheEntryRemoved;
      }
    }),
    interceptAIChatSession: builder.mutation<ResponseDto<ChatSessionDto>, number>({
      query: (chatSessionId) => ({
        url: `/omni_channel/intercept_ai_chat_session/${chatSessionId}`,
        method: 'post'
      }),
      invalidatesTags: ['omni_channel_chat_session_supervisor_inprogress']
    }),

    getCallSessionInprogress: builder.query<ResponsePagingDto<CallSessionDto>, FindAllCallSessionDto>({
      query: (params) => ({
        url: '/omni_channel/call_sessions/in_progress',
        method: 'get',
        params
      }),
      serializeQueryArgs: ({ endpointName }) => endpointName,
      merge: (currentCache, newItems, { arg }) => {
        if (!!arg.skip && arg.skip !== 0) {
          currentCache.data.rows = mergeArrays(currentCache.data.rows, newItems.data.rows, 'callSessionId');
        } else currentCache.data.rows = newItems.data?.rows;
        currentCache.data.count = newItems.data.count;
      },
      forceRefetch({ currentArg, previousArg }) {
        return (
          currentArg?.fromStartTime !== previousArg?.fromStartTime ||
          currentArg?.toStartTime !== previousArg?.toStartTime ||
          currentArg?.fromEndTime !== previousArg?.fromEndTime ||
          currentArg?.toEndTime !== previousArg?.toEndTime ||
          currentArg?.skip !== previousArg?.skip ||
          currentArg?.limit !== previousArg?.limit ||
          currentArg?.callSource !== previousArg?.callSource
        );
      },
      providesTags: ['omni_channel_call_session_inprogress'],
      async onCacheEntryAdded(arg, { cacheDataLoaded, updateCachedData, cacheEntryRemoved, getState }) {
        try {
          await cacheDataLoaded;
          onReceiveCallSessionAiProgressing((data) => {
            if (data && data.callSessionId) {
              debounce(() => {
                updateCachedData((draft) => {
                  const stateQueries = getState().omniChannelApi.queries;

                  const queryKey = 'getCallSessionInprogress' + (arg.endpoint || '');
                  const dataQueries = stateQueries[queryKey];
                  const originalArgs = dataQueries?.originalArgs as FindAllCallSessionDto;

                  if (originalArgs?.isHandleByAi) {
                    const callSessionId = data.callSessionId;
                    const existingIndex = draft.data.rows.findIndex(
                      (callSession) => callSession.callSessionId === callSessionId
                    );
                    if (data.callStatus === CallSessionStatus.PROCESSING) {
                      if (existingIndex !== -1) {
                        if (data.isAlerted) {
                          draft.data.rows.splice(existingIndex, 1);
                          draft.data.rows.unshift(data);
                        } else {
                          draft.data.rows[existingIndex] = { ...data };
                        }
                      } else {
                        if (data.isAlerted) {
                          draft.data.rows.unshift(data);
                        } else {
                          const findIndexToInsert = draft.data.rows.findIndex((cs) => !cs.isAlerted);
                          if (findIndexToInsert === -1) {
                            draft.data.rows.push(data);
                          } else {
                            draft.data.rows.splice(findIndexToInsert, 0, data);
                          }
                        }
                        draft.data.count++;
                      }
                    } else if (data.callStatus === CallSessionStatus.COMPLETED) {
                      // Xóa nếu không ở trạng thái PROCESSING
                      if (existingIndex !== -1) {
                        draft.data.rows.splice(existingIndex, 1);
                        draft.data.count--;
                      }
                    }
                  }
                });
              }, 100)();
            }
          });
        } catch (err) {
          console.error({ err });
        }

        await cacheEntryRemoved;
        offReceiveCallSessionAiProgressing();
      }
    }),

    getCallSessionHistories: builder.query<ResponsePagingDto<CallSessionDto>, FindAllCallSessionDto>({
      query: (params) => ({
        url: '/omni_channel/call_sessions/completed',
        method: 'get',
        params
      }),
      serializeQueryArgs: ({ endpointName }) => endpointName,
      merge: (currentCache, newItems, { arg }) => {
        if (!!arg.skip && arg.skip !== 0) {
          currentCache.data.rows = mergeArrays(currentCache.data.rows, newItems.data.rows, 'callSessionId');
        } else currentCache.data.rows = newItems.data?.rows;
        currentCache.data.count = newItems.data.count;
      },
      forceRefetch({ currentArg, previousArg }) {
        return (
          currentArg?.fromStartTime !== previousArg?.fromStartTime ||
          currentArg?.toStartTime !== previousArg?.toStartTime ||
          currentArg?.fromEndTime !== previousArg?.fromEndTime ||
          currentArg?.toEndTime !== previousArg?.toEndTime ||
          currentArg?.skip !== previousArg?.skip ||
          currentArg?.limit !== previousArg?.limit ||
          currentArg?.callSource !== previousArg?.callSource
        );
      },
      providesTags: ['omni_channel_call_session_histories'],
      async onCacheEntryAdded(arg, { cacheDataLoaded, updateCachedData, cacheEntryRemoved, getState }) {
        try {
          await cacheDataLoaded;
          onReceiveCallSessionAiProgressing((data) => {
            if (data && data.callSessionId) {
              debounce(() => {
                updateCachedData((draft) => {
                  const stateQueries = getState().omniChannelApi.queries;

                  const queryKey = 'getCallSessionHistories' + (arg.endpoint || '');
                  const dataQueries = stateQueries[queryKey];
                  const originalArgs = dataQueries?.originalArgs as FindAllCallSessionDto;

                  if (originalArgs?.isHandleByAi) {
                    const callSessionId = data.callSessionId;
                    const existingIndex = draft.data.rows.findIndex(
                      (callSession) => callSession.callSessionId === callSessionId
                    );
                    if (data.callStatus === CallSessionStatus.COMPLETED) {
                      if (existingIndex !== -1) {
                        // Thay thế nếu data có trong danh sách
                        draft.data.rows[existingIndex] = { ...data };
                      } else {
                        if (
                          (!originalArgs?.callSource || data.callSource === originalArgs?.callSource) &&
                          (!originalArgs?.fromStartTime || data.receivedAt >= originalArgs?.fromStartTime) &&
                          (!originalArgs?.toEndTime || data.receivedAt <= originalArgs?.toEndTime) &&
                          (!originalArgs?.keyword || data.customerPhoneNumber?.includes(originalArgs?.keyword))
                        ) {
                          draft.data.rows.push(data);
                          draft.data.count++;
                        }
                      }
                    }
                  }
                });
              }, 100)();
            }
          });
        } catch (err) {
          console.error({ err });
        }

        await cacheEntryRemoved;
        offReceiveCallSessionAiProgressing();
      }
    }),

    getOmniSpeakToTexts: builder.query<GetOmniSpeakToTextResponseDto, FindAllSpeakToTextDto>({
      query: ({ ...params }) => ({
        url: '/omni_channel/speak_to_texts',
        method: 'get',
        params
      }),

      async transformResponse(response: GetOmniSpeakToTextResponseDto) {
        return {
          ...response
        };
      },

      serializeQueryArgs: ({ endpointName, queryArgs }) => endpointName + queryArgs.callSessionId,
      merge: (currentCache, newItems, { arg }) => {
        if (arg?.startMessageId) {
          let currentRows = [...currentCache.data.rows];
          if (arg.sortOrder === OMNI_MESSAGE_QUERY_TYPE.NEWER) {
            currentRows =
              currentRows.length >= MESSAGE_LIMIT_CURRENT ? currentRows.slice(MESSAGE_LIMIT_LOAD) : currentRows;
            currentCache.data.rows = [...currentRows, ...newItems.data.rows];
          } else {
            currentRows =
              currentRows.length >= MESSAGE_LIMIT_CURRENT ? currentRows.slice(0, -MESSAGE_LIMIT_LOAD) : currentRows;
            currentCache.data.rows = [...newItems.data.rows, ...currentRows];
          }
          currentCache.data.count = newItems.data.count;
          currentCache.newMess = newItems.data.rows;
          return;
        }

        currentCache.data.count = newItems.data.count;
        currentCache.newMess = newItems.data.rows;
        currentCache.data.rows = newItems.data.rows;
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.callSessionId !== previousArg?.callSessionId;
      },
      providesTags: (result) =>
        result && result.data.rows.length > 0
          ? result.data.rows.map(({ speakToTextId, callSessionId }) => ({
              type: 'omni_channel_message_speak_to_texts',
              id: speakToTextId
            }))
          : ['omni_channel_message_speak_to_texts']
    }),

    updateCallSession: builder.mutation<ResponseDto<CallSessionDto>, UpdateCallSessionDto>({
      query: ({ callSessionId, ...data }) => ({
        url: `/omni_channel/call_sessions/${callSessionId}`,
        method: 'patch',
        data
      }),
      invalidatesTags: ['omni_channel_call_session_inprogress', 'omni_channel_call_session_histories']
    }),

    interceptAICallSession: builder.mutation<ResponseDto<CallSessionDto>, number>({
      query: (callSessionId) => ({
        url: `/omni_channel/intercept_ai_call_session/${callSessionId}`,
        method: 'post'
      }),
      invalidatesTags: ['omni_channel_call_session_inprogress']
    }),

    getChatSessionDetail: builder.query<ResponseDto<ChatSessionDto>, number>({
      query: (chatSessionId) => ({
        url: `/omni_channel/chat_sessions/${chatSessionId}`,
        method: 'get'
      }),
      providesTags: (result) => (result ? [{ type: 'chat_session_detail', id: result.data.chatSessionId }] : [])
    }),

    getCallSessionDetail: builder.query<ResponseDto<CallSessionDto>, number>({
      query: (callSessionId) => ({
        url: `/omni_channel/call_sessions/${callSessionId}`,
        method: 'get'
      }),
      providesTags: (result) => (result ? [{ type: 'call_session_detail', id: result.data.callSessionId }] : [])
    }),
    countCallSession: builder.query<ResponseDto<CountCallSessionResultDto>, CountCallSessionDto>({
      query: ({ ...params }) => ({
        url: '/omni_channel/count_call_session',
        method: 'get',
        params
      })
    })
  })
});

async function addMetadataToOmniMessages(rows: OmniChannelMessageDto[]) {
  return await Promise.all(
    rows.map(async (row) => {
      if (row.content && row.content.length > 0) {
        return await addMetadataOmniMessage(row);
      }
      return row;
    })
  );
  // return dummyOmniChannelMessages;
}

async function addMetadataOmniMessage(message: OmniChannelMessageDto) {
  const urls = extractUrls(message.content ?? '');
  if (urls.length > 0) {
    const metadataTag = await fetchMetadata(urls[0]);
    if (metadataTag) {
      const metadata = convertMetadata(metadataTag);
      return { ...message, metadata };
    }
  }
  return message;
}

function addMessageOmniChat(data: OmniChannelMessageDto, endpointName: any, arg?: Partial<FindAllOmniMessagesDto>) {
  return (dispatch: AppDispatch) => {
    dispatch(
      omniChannelApi.util.updateQueryData(endpointName, arg, (draftData: any) => {
        let currentRows = [...draftData.data.rows];
        currentRows = currentRows.length >= MESSAGE_LIMIT_CURRENT ? currentRows.slice(MESSAGE_LIMIT_LOAD) : currentRows;

        currentRows.push(data);
        draftData.data.count += 1;
        draftData.data.rows = currentRows;
      })
    );
  };
}

function updateMessageOmniChat(
  data: OmniChannelMessageDto,
  tempMessageId: number,
  endpointName: any,
  arg?: Partial<FindAllOmniMessagesDto>
) {
  return (dispatch: AppDispatch) => {
    dispatch(
      omniChannelApi.util.updateQueryData(endpointName, arg, (draftData: any) => {
        const idx = findIndex(draftData.data.rows || [], {
          messageId: tempMessageId
        });
        if (idx !== -1) {
          draftData.data.rows[idx] = {
            ...data
          };
        }
      })
    );
  };
}

function updateSpeakToTextsChat(
  data: SpeakToTextDto,
  tempMessageId: number,
  endpointName: any,
  arg?: Partial<FindAllSpeakToTextDto>
) {
  return (dispatch: AppDispatch) => {
    dispatch(
      omniChannelApi.util.updateQueryData(endpointName, arg, (draftData: any) => {
        const idx = findIndex(draftData.data.rows || [], {
          speakToTextId: tempMessageId
        });
        if (idx !== -1) {
          draftData.data.rows[idx] = {
            ...data
          };
        }
      })
    );
  };
}

function addSpeakToTextChat(data: SpeakToTextDto, endpointName: any, arg?: Partial<FindAllSpeakToTextDto>) {
  return (dispatch: AppDispatch) => {
    dispatch(
      omniChannelApi.util.updateQueryData(endpointName, arg, (draftData: any) => {
        let currentRows = [...draftData.data.rows];
        currentRows = currentRows.length >= MESSAGE_LIMIT_CURRENT ? currentRows.slice(MESSAGE_LIMIT_LOAD) : currentRows;

        currentRows.push(data);
        draftData.data.count += 1;
        draftData.data.rows = currentRows;
      })
    );
  };
}

function resetMessageOmniChat(endpointName: any, arg?: Partial<FindAllOmniMessagesDto>) {
  return (dispatch: AppDispatch) => {
    dispatch(
      omniChannelApi.util.updateQueryData(endpointName, arg, (draftData: any) => {
        draftData.data.count = 0;
        draftData.data.rows = [];
      })
    );
  };
}

export {
  resetMessageOmniChat,
  addMessageOmniChat,
  addMetadataOmniMessage,
  updateMessageOmniChat,
  updateSpeakToTextsChat,
  addSpeakToTextChat
};
export const {
  useLazyGetChatSessionsProcessingQuery,
  useLazyGetChatSessionsWaitingQuery,
  useGetChatSessionsWaitingQuery,
  useGetChatSessionsProcessingQuery,
  useGetChatSessionsHistoriesQuery,
  useLazyGetChatSessionsHistoriesQuery,
  useLazyGetChatSessionsSupervisorInprogressQuery,
  useGetChatSessionsSupervisorInprogressQuery,
  useLazyGetChatSessionsSupervisorHistoriesQuery,
  useGetChatSessionsSupervisorHistoriesQuery,
  useReceiveChatSessionMutation,
  useCompleteChatSessionMutation,
  useLazyGetOmniChatMessagesQuery,
  useReadOmniChatMessagesMutation,
  useSendOmniChatMessageMutation,
  useGetGroupCallsQuery,
  useGetFanpagesQuery,
  useGetGatewayConfigQuery,
  useGetSoftPhoneTokenMutation,
  useGetAgentStatusQuery,
  useLazyGetAgentStatusQuery,
  useUnpauseQueueAgentMutation,
  usePauseQueueAgentMutation,
  useLazyListQueueQuery,
  useLazyListQueueNameQuery,
  useAddQueueMemberMutation,
  useRemoveQueueMemberMutation,
  useListQueueNameQuery,
  useLazyGetCallDetailsQuery,
  useGetWaitingCallsQuery,
  useLazyCountChatSessionQuery,
  useAssignChatSessionMutation,
  useInterceptAIChatSessionMutation,
  useTransferChatSessionMutation,
  useGetAgentsQuery,
  useLikeCommentMutation,
  useRemoveCommentMutation,
  useHideCommentMutation,
  useCreateChatMessengerMutation,
  useUpdateChatAgentStatusMutation,
  useGetChatAgentStatusQuery,
  useGetCallHistoryQuery,
  useGetVoiceRecordQuery,
  useLazyGetVoiceRecordQuery,
  useLazyGetCustomersQuery,
  useGetSourceOptionsQuery,
  useCountAssignerProcessingChatSessionsQuery,
  useGetChatMonitoringUserQuery,
  useLazyGetTicketsAIAgentQuery,
  useGetCallSessionInprogressQuery,
  useGetCallSessionHistoriesQuery,
  useLazyGetCallSessionHistoriesQuery,
  useLazyGetOmniSpeakToTextsQuery,
  useUpdateCallSessionMutation,
  useInterceptAICallSessionMutation,
  useGetChatSessionDetailQuery,
  useLazyGetChatSessionDetailQuery,
  useLazyGetCallSessionDetailQuery,
  useCountCallSessionQuery,
  useCountChatSessionQuery
} = omniChannelApi;
